import {
  html,
  ovl,
  SYSConfig,
  SYSForm,
  SYSTableDefIds,
  TemplateResult,
} from "../.."
import { Data } from "../../dataapi"
import { _HasPermission } from "../../global/globals"
import {
  HookFormAfterCancel,
  HookFormAfterCancel_Type,
  HookFormBeforeCancel,
  HookFormBeforeCancel_ReturnType,
  HookFormBeforeCancel_Type,
  HookTableColumnFn,
  HookTableColumnFn_Type,
  HookTableFn,
  HookTableFn_Type,
  HookTablePrepareFn,
  HookTablePrepareFn_ReturnType,
  HookTablePrepareFn_Type,
  HookTableSchemaAssign,
  HookTableSchemaAssign_Type,
  TableRowFnProcessFeedback_Type,
  TableRowFnProcessRawFeedback_Type,
} from "../../global/hooks"
import { T } from "../../textapi"
import { DialogClose, DialogOkCancel, DialogOpen } from "../dialog/dialogapi"
import { InitFormType } from "../forms/actions"

import { SYSField, SYSFormState } from "../forms/fieldapi"
import { FormIsDirty, FormRemove } from "../forms/formapi"
import { Schema } from "../forms/sysformelement"

import {
  _LocalDataSyncType,
  _TableEditSaveResultsType,
  _TableEditSaveRowsHelper,
  FileUploadContainer,
  TableInitResetStrategy,
  TableInitType,
  TableRefreshType,
} from "./actions"
import {
  _GetFieldList,
  _TableFilterFn,
  getFormFieldsFromColumns,
  getListValue,
  GetRowDisplayValue,
  GetRowRawValue,
  GetTableKeys,
} from "./helpers"
import { _HookTableGroupDefId } from "./rowwrapper"

import {
  _SelectionClippingType,
  _TableGetGroupInitAggregateResult,
  _TableGroupAggregateResultType,
  ControlType,
  OvlSysColumn,
  SelectRowDefOptions,
  SortDirection,
  StaticFilter,
  SYSTableData,
  SYSTableDef,
} from "./table"
import { _BaseTableContextForColumnFilter } from "./tablerowheadermenu/tablesettings/columnfilter/helpers"
import {
  SYSTableSettings_ColumnFilter,
  tblTableSettings_ColumnFilter,
} from "./tablerowheadermenu/tablesettings/columnfilter/tabledef"

export type _RefreshTableOptions<RowType = any> = {
  showSnack?: boolean
  localData?: any[]

  staticFilter?: {}
  serverFilter?: {}
  customFilter?: { [key: string]: { active: boolean } }
  fetchData?: boolean
  resetStrategy?: TableInitResetStrategy
  forceFilterKeys?: _GetTableOptions<RowType>
  forceFilterContextTitle?: string
  doNotRebuild?: boolean

  // override localDataSync if default not applicable:
  // default:
  // if localData and tabledef has an endpont => refresh
  // all other cases => removeclientdata
  // logic for removeclientdata:
  // check clientdata filtered the same way as serverresult (staticFilter & serverFilter)
  // if clientdata doesn't contain serverdata remove clientdata
  // if there is no filter at all just all data gets checked
  localDataSync?: _LocalDataSyncType
}

export type _InitTableOptions = {
  resetStrategy?: TableInitResetStrategy
  staticFilter?: StaticFilter
  forceInit?: boolean
}

export type _AddRowToTableOptions = {
  useDefaultSnack?: boolean
  files?: FileUploadContainer[]
  multi?: { isFirst: boolean; isLast: boolean }
  ignoreNeedsRefresh?: boolean
  // if ou need to update the primary key, we need the originalId
  originalIdIfUpdate?: any
}

export const TableFnCanAdd = async (
  defId: SYSTableDefIds
): Promise<boolean> => {
  // use hook to check if Table has Add Fn
  // usecase BAR: GENAttachments drag and drop when no
  let tb = TableGet(defId)
  let def = tb.def
  if (def.features.add && _HasPermission(def.permission, "A")) {
    // def.feature maybe not set because table not initialised. we then just use def.id as default init does.
    let customFnHook =
      ovl.getActions().app.features?.[def.feature ? def.feature : def.id]
        ?.hooks?.[HookTablePrepareFn]
    let customFnHookResult: HookTablePrepareFn_ReturnType
    if (customFnHook) {
      customFnHookResult = await customFnHook(<HookTablePrepareFn_Type>{
        data: tb.dataDef,
        def,
        values: undefined,
        mode: "HeaderMenu",
        type: "Table",
        columnKey: undefined,
      })
    }

    return (
      customFnHookResult === undefined ||
      customFnHookResult?.["OvlAdd"]?.visibility === "Visible"
    )
  } else {
    return false
  }
}

export const TableRefresh = async <RowType = any>(
  defId: SYSTableDefIds,
  options?: _RefreshTableOptions<RowType>
): Promise<_TableGetReturnType<RowType>> => {
  let state = ovl.getState()
  if (state.ovl.uiState.appIsScreenNavigateInRehydrating) {
    return TableGet(defId)
  }

  let data = <SYSTableData>SYSConfig.app.tableDefIds[defId].getData(state)
  let localData = options?.localData

  let refresh: TableRefreshType = {
    ignoreRefreshedMessageSnack: options?.showSnack === false,
    data,
    defId,
    forceServerDataRefresh: !options || options.fetchData !== false,
    localData,
    // provide serverfilter if you would like to refresh data (deleted rows will not be deleted in client)
    serverFilter: options?.serverFilter,
    // staticfilter is important to manage vies like SalesOrder -> SalesOrder Lines where would have the DocEntry as static filter in Document Lines View
    // a staticFilter automatically deletes rows on the client if it was deleted by other user
    staticFilter: options?.staticFilter,
    customFilter: options?.customFilter,
    forceFilterKeys: options?.forceFilterKeys,
    forceFilterContextTitle: options?.forceFilterContextTitle,
    doNotRebuild: options?.doNotRebuild,
    localDataSync: options?.localDataSync,
  }

  // if (options?.resetStrategy && (options.resetStrategy.ClearDataView || options.resetStrategy.Columns)) {
  //   TableInit(defId, { resetStrategy: options.resetStrategy })
  // } else {

  //@maybe instead of tablerebuild when using staticfilter
  // do a clearviewdata inside init in one step

  if (options?.resetStrategy) {
    await TableInit(defId, {
      resetStrategy: options.resetStrategy,
      staticFilter: options?.staticFilter,
    })
  }
  await ovl.getActions().ovl.table._TableRefresh(refresh)
  return TableGet<RowType>(defId)
}
type _TableRebuildOptions<RowType = any> = {
  showProcess?: boolean
  staticFilter?: any
  doNotPersistFilterSort?: boolean

  forceFilterKeys?: _GetTableOptions<RowType>
  forceFilterContextTitle?: string
  selectionClipping?: _SelectionClippingType
}
export const TableRebuild = async <RowType = any>(
  defId: SYSTableDefIds,
  options?: _TableRebuildOptions
): Promise<_TableGetReturnType<RowType>> => {
  if (ovl.getState().ovl.uiState.appIsScreenNavigateInRehydrating) {
    return TableGet(defId)
  }

  await ovl.getActions().ovl.table._TableRebuild({
    defId,
    data: SYSConfig.app.tableDefIds[defId].getData(ovl.getState()),
    showProcess: options?.showProcess,
    staticFilter: options?.staticFilter,
    doNotPersistFilterSort: options?.doNotPersistFilterSort,

    forceFilterKeys: options?.forceFilterKeys,
    forceFilterContextTitle: options?.forceFilterContextTitle,
    selectionClipping: options?.selectionClipping,
  })
  return TableGet<RowType>(<SYSTableDefIds>defId)
}

export type _GetTableOptions<T> = {
  byKeys?:
    | "StaticFiltered"
    | "Visible"
    | "All"
    | "Selected"
    | "SelectedTimeAsc"
    | "SelectedTimeDesc"
    | "RowControlVisible"
  byIdValue?: any
  byCustomFilter?: (row: T, key: string) => boolean
  byCustomFilterInVisible?: (row: T, key: string) => boolean
  byFilter?: { [key: string]: any }
  byKey?: string
  onlyUseTheseKeys?: any[]
}

export type _FormStateGetForRowKeyOptions = {
  onlyIfInEditMode?: boolean
}

export type _TableGetReturnType<T = any> = {
  dataDef: SYSTableData
  def: SYSTableDef
  getKeys: (currentoptions?: _GetTableOptions<T>) => string[]
  rows: { [key: string]: T }
  formStateGetForRowKey: (
    rowkey: string,
    options?: _FormStateGetForRowKeyOptions
  ) => SYSFormState
  formStateInitForRowKey: (rowkey: string) => Promise<SYSFormState>
  formEditRow: (
    key: string,
    options?: _TableFormEditRowOptions
  ) => Promise<SYSFormState>
  formRemoveRowFormState: (rowKey: string) => void
  init: (options?: _InitTableOptions) => Promise<_TableGetReturnType>
  schemaGet: () => Promise<{ [key: string]: Schema }>
  refresh: (options?: _RefreshTableOptions<T>) => void
  rebuild: (options?: _TableRebuildOptions<T>) => void
  staticFilterSetEntry: (
    filterKey: string,
    filterValue: any
  ) => { [key in keyof T]: any }
  staticFilterRemoveEntry: (filterKey: string) => { [key in keyof T]: any }
  staticFilterGet: () => { [key in keyof T]: any }
  staticFilterReset: () => { [key in keyof T]: any }
  saveRow: (
    row: {},
    options?: _AddRowToTableOptions
  ) => Promise<{ newId: any; rowKey: string }>
  removeRowFromData: (key: string) => void
  columnFilterGet: (columnKey: string) => { list: any[]; exclusive: boolean }
}

export const TableGet = <T = any>(
  defId: SYSTableDefIds,
  options?: _GetTableOptions<T>
): _TableGetReturnType<T> => {
  // takes into account staticfilter
  if (!options) {
    options = {}
  }
  let state = ovl.getState()
  let dataDef: SYSTableData = SYSConfig.app.tableDefIds[defId].getData(state)
  let def = dataDef.tableDef[defId]

  return {
    dataDef,
    getKeys: (currentoptions?: _GetTableOptions<T>) => {
      let opt = options
      if (currentoptions) {
        opt = currentoptions
      }
      return GetTableKeys(def, dataDef, opt)
    },
    rows: dataDef.data,
    def: dataDef.tableDef[defId],
    formStateGetForRowKey: (
      rowkey: string,
      options?: _FormStateGetForRowKeyOptions
    ) => TableFormStateGetForRowKey(defId, rowkey, options),
    formStateInitForRowKey: async (rowkey: string) =>
      await TableFormStateInitForRowKey(defId, rowkey),
    formEditRow: async (rowkey: string, options?: _TableFormEditRowOptions) =>
      await TableFormEditRow(defId, rowkey, options),
    formRemoveRowFormState: (rowkey: string) => {
      let formType: SYSForm = "SYSTableRowEdit"
      let formid = `${def.id}_${rowkey}`
      FormRemove(formType, formid)
    },
    init: async (options?: _InitTableOptions) =>
      await TableInit(defId, options),
    schemaGet: async () => await TableSchemaGet(defId),
    rebuild: async (options?: _TableRebuildOptions) =>
      await TableRebuild(defId, options),
    refresh: async (options?: TableRefreshType) =>
      await TableRefresh(defId, options),
    staticFilterSetEntry: (filterKey: string, filterValue: any) => {
      return TableStaticFilterSetEntry(defId, filterKey, filterValue)
    },
    staticFilterRemoveEntry: (filterKey: string) => {
      return TableStaticFilterRemoveEntry(defId, filterKey)
    },
    staticFilterReset: () => {
      return TableStaticFilterReset(defId)
    },
    staticFilterGet: () => {
      return TableStaticFilterGet(defId)
    },
    saveRow: async (row: {}, options: _AddRowToTableOptions) => {
      return await TableSaveRow(defId, row, options)
    },
    columnFilterGet: (columnKey: string) => {
      return TableColumnFilterGet(defId, columnKey)
    },

    removeRowFromData: (key: string) => {
      TableRemoveRowFromData(defId, key)
    },
  }
}

export const TableColumnList = (
  tableDefId: SYSTableDefIds,
  columnKey: string
) => {
  return _GetFieldList(undefined, columnKey, undefined, tableDefId)
}

export const TableColumnFilterGet = <RowType = any>(
  defId: SYSTableDefIds,
  columnKey: keyof RowType
): { list: any[]; exclusive: boolean } => {
  let def = TableGet(defId).def
  let filter = JSON.parse(
    JSON.stringify(def.uiState.filter.columns[<string>columnKey].columnFilter)
  )
  return { list: filter.list, exclusive: filter.exclusive }
}

export type _TableColumnFilterOptionsType = {
  exclusive?: boolean
  rebuild?: boolean
  _innerEvent?: boolean
}

export const TableColumnFilter = async (
  defId: SYSTableDefIds,
  columnKey: string,
  list: any[],
  options?: _TableColumnFilterOptionsType
): Promise<boolean> => {
  if (list === undefined) {
    list = []
  }
  let def = TableGet(defId).def
  if (
    !def.uiState.filter.columns[columnKey] ||
    !def.uiState.filter.columns[columnKey].columnFilter
  ) {
    console.error(`SYS Error: columnfilter column ${columnKey} not found`)
    return false
  }
  let tableFn = ovl.getActions().app.features[def.feature]?.hooks?.[HookTableFn]
  if (tableFn) {
    if (
      await tableFn(<HookTableFn_Type>{
        FnType: "ColumnFilter",
        before: true,
        id: columnKey,
        selection: list,
        val1: options?.exclusive === true,
        innerEvent: options?._innerEvent === true,
      })
    ) {
      return false
    }
  }
  await ovl.getActions().ovl.internal._TableSelectColumnFilter({
    list,
    columnKey,
    def,
    exclusive: options?.exclusive === true,
  })

  if (tableFn) {
    await tableFn(<HookTableFn_Type>{
      FnType: "ColumnFilter",
      before: false,
      id: columnKey,
      selection: list,
      val1: options?.exclusive === true,
      innerEvent: options?._innerEvent === true,
    })
  }

  if (options?.rebuild) {
    if (columnFilterRebuildDebounce) {
      clearTimeout(columnFilterRebuildDebounce)
    }
    columnFilterRebuildDebounce = setTimeout(async () => {
      await TableRebuild(defId)
    }, 50)
  }

  return true
}
let columnFilterRebuildDebounce: any
export const TableCustomFilter = async (
  defId: SYSTableDefIds,
  key: string,
  active: boolean,
  options?: { _innerEvent?: boolean }
): Promise<boolean> => {
  let dataDef: SYSTableData = SYSConfig.app.tableDefIds[defId].getData(
    ovl.getState()
  )
  let def = dataDef.tableDef[defId]

  if (!def.uiState.filter.filterCustom[key]) {
    console.warn(`SYS Warning: customfilter ${key} not found anymore`)
    return true
  }
  // if (def.uiState.filter.filterCustom[key].active === active) {
  //   return false
  // }
  let tableFn = ovl.getActions().app.features[def.feature]?.hooks?.[HookTableFn]
  if (tableFn) {
    if (
      await tableFn(<HookTableFn_Type>{
        FnType: "CustomFilter",
        before: true,
        innerEvent: options?._innerEvent === true,
        id: key,
        val1: active,
      })
    ) {
      return false
    }
  }
  await ovl.getActions().ovl.internal._TableSelectCustomFilter({
    id: key,
    def,
    active,
  })
  if (tableFn) {
    await tableFn(<HookTableFn_Type>{
      FnType: "CustomFilter",
      before: false,
      innerEvent: options?._innerEvent === true,
      id: key,
      val1: active,
    })
  }
  return true
}
export const TableAttributeFilter = async (
  defId: SYSTableDefIds,
  attributeDefKey: string,
  attributeKey: string,
  active: boolean,
  options?: { _innerEvent?: boolean }
): Promise<boolean> => {
  let dataDef: SYSTableData = SYSConfig.app.tableDefIds[defId].getData(
    ovl.getState()
  )
  let def = dataDef.tableDef[defId]
  if (!def.uiState.filter.filterAttributes[attributeDefKey]) {
    console.warn(
      `SYS Warning: attributefilter ${attributeDefKey} not found anymore`
    )
    return true
  }
  // if (def.uiState.filter.filterCustom[key].active === active) {
  //   return false
  // }
  let tableFn = ovl.getActions().app.features[def.feature]?.hooks?.[HookTableFn]
  if (tableFn) {
    if (
      await tableFn(<HookTableFn_Type>{
        FnType: "AttributeFilter",
        before: true,
        innerEvent: options?._innerEvent === true,
        id: attributeDefKey,
        val1: attributeKey,
        val2: active,
      })
    ) {
      return false
    }
  }
  await ovl.getActions().ovl.internal._TableSelectAttributeFilter({
    active,
    attributeDefKey,
    attributeKey,
    def,
  })
  if (tableFn) {
    await tableFn(<HookTableFn_Type>{
      FnType: "AttributeFilter",
      before: false,
      innerEvent: options?._innerEvent === true,
      id: attributeDefKey,
      val1: attributeKey,
      val2: active,
    })
  }
  return true
}

export type _TableFilterSelectedOptionsType = { _innerEvent: boolean }
export const TableFilterSelected = async (
  defId: SYSTableDefIds,
  options?: _TableFilterSelectedOptionsType
): Promise<boolean> => {
  let dataDef: SYSTableData = SYSConfig.app.tableDefIds[defId].getData(
    ovl.getState()
  )
  let def = dataDef.tableDef[defId]
  let tableFn = ovl.getActions().app.features[def.feature]?.hooks?.[HookTableFn]
  if (tableFn) {
    if (
      await tableFn(<HookTableFn_Type>{
        FnType: "FilterSelected",
        before: true,
        innerEvent: options?._innerEvent === true,
      })
    ) {
      return false
    }
  }
  ovl.getActions().ovl.internal._TableFilterSelected({ def, data: dataDef })
  if (tableFn) {
    await tableFn(<HookTableFn_Type>{
      FnType: "FilterSelected",
      before: false,
      innerEvent: options?._innerEvent === true,
    })
  }
  return true
}

export type _CustomSortTableOptionsType = {
  key: string
}

export const TableCustomSort = async (
  defId: SYSTableDefIds,
  options: _CustomSortTableOptionsType
): Promise<boolean> => {
  let dataDef: SYSTableData = SYSConfig.app.tableDefIds[defId].getData(
    ovl.getState()
  )
  let def = dataDef.tableDef[defId]
  let tableFn = ovl.getActions().app.features[def.feature]?.hooks?.[HookTableFn]
  if (tableFn) {
    if (
      await tableFn(<HookTableFn_Type>{
        FnType: "CustomSort",
        before: true,
        id: options.key,
      })
    ) {
      return false
    }
  }
  let sortCustom = def.uiState.sort.sortCustom

  if (sortCustom.selected === options.key) {
    return true
  }

  ovl.getActions().ovl.internal._TableSelectCustomSort({
    id: options.key,
    def,
  })
  TableRebuild(defId, { showProcess: true })

  if (tableFn) {
    await tableFn(<HookTableFn_Type>{
      FnType: "CustomSort",
      before: false,
      id: options.key,
    })
  }
  return true
}

export type _SelectAllTableOptionsType = {
  select: boolean
}

export const TableSelectAll = async (
  defId: SYSTableDefIds,
  options: _SelectAllTableOptionsType
): Promise<boolean> => {
  let dataDef: SYSTableData = SYSConfig.app.tableDefIds[defId].getData(
    ovl.getState()
  )
  let def = dataDef.tableDef[defId]
  let tableFn = ovl.getActions().app.features[def.feature]?.hooks?.[HookTableFn]
  if (tableFn) {
    if (
      await tableFn(<HookTableFn_Type>{
        FnType: "SelectAll",
        before: true,
        val1: options.select,
      })
    ) {
      return false
    }
  }

  ovl.getActions().ovl.internal._TableSelectAll({
    tableDefId: def.id,
    select: options.select,
  })

  if (tableFn) {
    await tableFn(<HookTableFn_Type>{
      FnType: "SelectAll",
      before: false,
      val1: options.select,
    })
  }
  return true
}

export type ColumnFnTableOptionsType = {
  key: string
  Selection?: boolean
  _innerEvent?: boolean
}

export const TableCustomColumnFn = async (
  defId: SYSTableDefIds,
  options: ColumnFnTableOptionsType
): Promise<boolean> => {
  let dataDef: SYSTableData = SYSConfig.app.tableDefIds[defId].getData(
    ovl.getState()
  )
  let def = dataDef.tableDef[defId]
  let hooks = ovl.getActions().app.features[def.feature]?.hooks
  let tableFn = hooks?.[HookTableFn]
  if (tableFn) {
    if (
      await tableFn(<HookTableFn_Type>{
        FnType: "CustomColumnFunction",
        before: true,
        id: options.key,
        selection: options.Selection,
        innerEvent: options._innerEvent === true,
      })
    ) {
      return false
    }
  }

  if (!options.Selection) {
    let fnName = HookTableColumnFn
    if (hooks[fnName]) {
      hooks[fnName](<HookTableColumnFn_Type>{
        columnKey: def.uiState.headerSelected,
        fnKey: options.key,
        def,
        data: dataDef,
        innerEvent: options._innerEvent,
      })
    } else {
      console.error(`ovl error: ColumnFnHook ${fnName} not found`)
    }
    //@@todo, check use case for MultipleCustomColumnFunction?
    // looks like for now its not used
    // and it seems not be handled for columnfunctions but only row functions inside _TableMultipleCustomFuncion Action
  } else {
    ovl.getActions().ovl.internal._TableMultipleCustomFunction({
      def,
      data: dataDef,
      customFnId: options.key,
    })
  }
  if (tableFn) {
    await tableFn(<HookTableFn_Type>{
      FnType: "CustomColumnFunction",
      before: false,
      id: options.key,
      selection: options.Selection,
      innerEvent: options._innerEvent === true,
    })
  }
  return true
}

export type _FilterTableOptionsType = {
  filterValue: string
}

export const TableFilter = async (
  defId: SYSTableDefIds,
  options: _FilterTableOptionsType
): Promise<boolean> => {
  let dataDef: SYSTableData = SYSConfig.app.tableDefIds[defId].getData(
    ovl.getState()
  )
  let def = dataDef.tableDef[defId]
  let tableFn = ovl.getActions().app.features[def.feature]?.hooks?.[HookTableFn]
  if (tableFn) {
    if (
      await tableFn(<HookTableFn_Type>{
        FnType: "Filter",
        before: true,
        id: options.filterValue,
      })
    ) {
      return false
    }
  }
  ovl.getActions().ovl.table._TableFilter({
    def,
    data: dataDef,
    value: options.filterValue,
  })
  if (tableFn) {
    await tableFn(<HookTableFn_Type>{
      FnType: "Filter",
      before: false,
      id: options.filterValue,
    })
  }
  return true
}

export type _ColumnSortTableOptions = {
  columnKey: string
  sortByRawValue?: boolean
  sortOrder?: SortDirection
}

export const TableColumnSort = async (
  defId: SYSTableDefIds,
  options: _ColumnSortTableOptions
): Promise<boolean> => {
  let dataDef: SYSTableData = SYSConfig.app.tableDefIds[defId].getData(
    ovl.getState()
  )
  if (options.sortByRawValue === undefined) {
    options.sortByRawValue = false
  }
  if (options.sortOrder === undefined) {
    options.sortOrder = "asc"
  }

  let def = dataDef.tableDef[defId]
  let tableFn = ovl.getActions().app.features[def.feature]?.hooks?.[HookTableFn]
  if (tableFn) {
    if (
      await tableFn(<HookTableFn_Type>{
        FnType: "ColumnSort",
        before: true,
        id: options.columnKey,
        val1: options.sortOrder,
        val2: options.sortByRawValue,
      })
    ) {
      return false
    }
  }
  ovl.getActions().ovl.table._TableSort({
    def,
    data: dataDef,
    key: options.columnKey,
    useValue: options.sortByRawValue,
    ascending: options.sortOrder === "asc",
  })
  TableRebuild(defId, { showProcess: true })
  if (tableFn) {
    await tableFn(<HookTableFn_Type>{
      FnType: "ColumnSort",
      before: false,
      id: options.columnKey,
      val1: options.sortOrder,
      val2: options.sortByRawValue,
    })
  }
  return true
}

export const TableInit = async <RowType = any>(
  defId: SYSTableDefIds,
  options?: _InitTableOptions
): Promise<_TableGetReturnType<RowType>> => {
  if (ovl.getState().ovl.uiState.appIsScreenNavigateInRehydrating) {
    return TableGet(defId)
  }

  let dataDef = SYSConfig.app.tableDefIds[defId].getData(ovl.getState())
  let def = dataDef.tableDef[defId]
  if (options?.forceInit) {
    def.initialised = false
  }
  let init: TableInitType = {
    data: dataDef,
    defId,
    resetStrategy: options?.resetStrategy,
    staticFilter: options?.staticFilter,
  }

  await ovl.getActions().ovl.table._TableInit(init)
  return TableGet(defId)
}

export type TableFormElementType = {
  mode: { byStaticFilter?: { [key: string]: any }; byKey?: any }
  tableDefId: SYSTableDefIds
  formId: string
  options?: { saveOnDisconnect?: boolean }
}

export const TableRowFnExecute = async (
  tableDefId: SYSTableDefIds,
  fnKey: string,
  keysToProcess: string[]
) => {
  let tb = TableGet(tableDefId)
  await ovl.getActions().ovl.internal._TableMultipleCustomFunction({
    def: tb.def,
    data: tb.dataDef,
    customFnId: fnKey,
    customSelection: keysToProcess,
  })
}

export const TableRowFnAddFeedback = (
  tableDefId: SYSTableDefIds,
  fnKey: string,
  feedback: TableRowFnProcessRawFeedback_Type
) => {
  let tb = TableGet(tableDefId)
  let key = feedback.id
  if (!tb.rows[key]) {
    key = tb.dataDef.index[key]
  }
  let ovlSysState = tb.rows[key]["OvlSysState"] as OvlSysColumn

  let now = new Date().getTime()
  ovlSysState.lastFeedbackTS = now
  ovlSysState.fnFeedback[fnKey] = {
    ts: now,
    type: feedback.type,
    code: feedback.code,
    fnId: fnKey,
    id: key,
    text: feedback.text,
    defId: tableDefId,
  }
}

export type _TableEditSaveRowOptionsType = {
  useDefaultSnack?: boolean
}
export const TableEditSaveRow = async (
  formState: SYSFormState,
  options?: _TableEditSaveRowOptionsType
): Promise<_TableEditSaveResultsType> => {
  let tableDef = TableGet(formState.tableDefId)
  let res = await _TableEditSaveRowsHelper(
    [formState.tableRowKey],
    tableDef.def,
    tableDef.dataDef,
    formState,
    ovl.getState(),
    ovl.getActions(),
    undefined,
    {},
    !options?.useDefaultSnack
  )
  if (
    formState.tableDataMode === "Add" &&
    res.results.filter(
      (f) => f.key === formState.tableRowKey && f.type === "error"
    ).length === 0
  ) {
    formState.tableDataMode = "Update"
    tableDef.dataDef.data[formState.tableRowKey].OvlSysState.editRow.mode =
      undefined
  }
  return res
}

export const TableSaveRow = async (
  defId: SYSTableDefIds,
  rowToSave: {},

  options?: _AddRowToTableOptions
): Promise<{ newId: any; rowKey: string }> => {
  let data = SYSConfig.app.tableDefIds[defId].getData(ovl.getState())
  return await ovl.getActions().ovl.table._TableDirectSaveRow({
    data,
    defId,
    rowToSave,
    noSnack: !options?.useDefaultSnack,
    files: options?.files,
    multi: options?.multi,
    ignoreNeedsRefresh: options?.ignoreNeedsRefresh,
    originalIdIfUpdate: options?.originalIdIfUpdate,
  })
}

export const _TableDefSelectRow = async (
  tableDef: SYSTableDef,
  dataDef: SYSTableData,
  key: string,
  options?: SelectRowDefOptions
) => {
  ovl.getActions().ovl.table._TableSelectRow({
    def: tableDef,
    dataDef,
    key,
    options,
  })
}
export const TableSelectRow = (
  tableDefId: SYSTableDefIds,
  key: string,
  options?: SelectRowDefOptions
) => {
  let tableDef = TableGet(tableDefId)
  return _TableDefSelectRow(tableDef.def, tableDef.dataDef, key, options)
}

export type _EditTableRowOptions = {
  key?: any
}

export type _TableFormEditRowOptions = {
  forceInline?: boolean
}

export const TableFormEditRow = async (
  defId: SYSTableDefIds,
  key?: any,
  options?: _TableFormEditRowOptions
) => {
  let data: SYSTableData = SYSConfig.app.tableDefIds[defId].getData(
    ovl.getState()
  )
  if (key === undefined) {
    key = Object.keys(data.data)[0]
  }

  return <SYSFormState>(
    (<unknown>await ovl.getActions().ovl.table._TableEditRow({
      data,
      def: data.tableDef[defId],
      key,
      // always when we call editform from api we need a bigform
      // because we cannot be sure if the user is on the table or called it from somewhere where table is not visible
      forceBigForm: !options?.forceInline,
    }))
  )
}

export const TableFormStateGetForRowKey = (
  defId: SYSTableDefIds,
  key?: any,
  options?: _FormStateGetForRowKeyOptions
): SYSFormState => {
  let tb = TableGet(defId)
  let def = tb.def
  let data = tb.dataDef
  if (key === undefined) {
    key = tb.getKeys()[0]
  }
  let instanceId = `${def.id}_${key}`
  let formsState = ovl.getState().ovl.forms.SYSTableRowEdit
  let colSysState = data.data[key].OvlSysState as OvlSysColumn
  if (formsState[instanceId]) {
    if (options?.onlyIfInEditMode) {
      if (colSysState.editRow.selected) {
        return formsState[instanceId]
      }
    } else {
      return formsState[instanceId]
    }
  }
}

export const TableFormStateInitForRowKey = async (
  defId: SYSTableDefIds,
  key?: any
): Promise<SYSFormState> => {
  let tb = TableGet(defId)
  let def = tb.def
  let data = tb.dataDef
  if (key === undefined) {
    key = tb.getKeys()[0]
  }
  let instanceId = `${def.id}_${key}`
  let isInline = tb.def.options.edit?.editType === "inline"
  let formFields = getFormFieldsFromColumns(def, tb.rows[key], isInline)

  let initForm: InitFormType = {
    tableRowKey: key,
    fields: formFields,
    formType: "SYSTableRowEdit",
    formId: instanceId,
    feature: def.feature,
    forceKeepFormStateOnCancel: def.options.forceKeepFormStateOnCancel,
    doFormChangedForEveryFieldInInit:
      def.options.edit.doFormChangedForEveryFieldInInit,
    forceOverwrite: false,
    tableDefId: def.id,
    tableDataMode: "Update",
    isInline,
    row: tb.rows[key],
    initialFocusFieldKey: undefined,
    useAlignRight: def.options.controlsRendering.useAlignRight,
  }
  let formState = await ovl.getActions().ovl.form._InitForm(initForm)
  return formState
}

export type _TableEditRowOptionsType = {
  formType: SYSForm
  formId: any
}

export type _TableAddRowReturnType<RowType> = {
  newRow: { [key in keyof RowType]: any }
  key: string
  fieldKeyToFocus?: keyof RowType
}

export const TableFormAddRow = async (defId: SYSTableDefIds) => {
  let data: SYSTableData = SYSConfig.app.tableDefIds[defId].getData(
    ovl.getState()
  )

  let def = data.tableDef[defId]

  let addRes = await ovl.getActions().ovl.table._TableAddRow({
    data,
    def,
  })

  return <SYSFormState>(
    (<unknown>await ovl.getActions().ovl.table._TableEditRow({
      data,
      def: data.tableDef[defId],
      key: addRes.key,
    }))
  )
}

export type _CellValueOptionsType = {
  useDisplayValue?: boolean
  lookupColumnKey?: string
  rowKeyMode?: "id" | "key"
}

export type _SetCellValueOptionsType = {
  rowKeyMode?: "id" | "key"
}

export const TableSetCellVal = <RowType = any>(
  tableDefId: SYSTableDefIds,
  rowKey: any,
  columnKey: keyof RowType,
  value: any,
  options?: _SetCellValueOptionsType
): any => {
  let rowKeyMode = options?.rowKeyMode
  if (!rowKeyMode) {
    rowKeyMode = "key"
  }
  let data: SYSTableData = SYSConfig.app.tableDefIds[tableDefId].getData(
    ovl.getState()
  )
  data.data[rowKeyMode === "id" ? data.index[rowKey] : rowKey][columnKey] =
    value
}

export const TableCellVal = <RowType = any>(
  tableDefId: SYSTableDefIds,
  rowKey: any,
  columnKey: keyof RowType,
  options?: _CellValueOptionsType
): any => {
  let val

  let rowKeyMode = options?.rowKeyMode
  if (!rowKeyMode) {
    rowKeyMode = "key"
  }

  let data: SYSTableData = SYSConfig.app.tableDefIds[tableDefId].getData(
    ovl.getState()
  )

  let key = rowKeyMode === "id" ? data.index[rowKey] : rowKey
  val = data.data[key]?.[columnKey]
  let columnDef = data.tableDef[tableDefId].columns[<string>columnKey]
  let isCalculatedColumn = columnDef.schema?.isCalculatedColumn
  if (!isCalculatedColumn && val === undefined) {
    return null
  }
  let useDisplayValue = options?.useDisplayValue

  let feature = data.tableDef[tableDefId].feature
  let lookupColumnKey = options?.lookupColumnKey

  if (!columnDef) {
    return val
  } else if (useDisplayValue) {
    return GetRowDisplayValue(
      data.tableDef[tableDefId],
      <string>columnKey,
      data.data[rowKey],
      rowKey,
      "Table"
    )
  } else {
    if (!options?.lookupColumnKey || !columnDef.list) {
      if (isCalculatedColumn) {
        val = GetRowRawValue(
          data.tableDef[tableDefId],
          <string>columnKey,
          data.data[rowKey],
          rowKey
        )
      }
      return val
    }
    return getListValue(
      <string>columnKey,
      val,
      feature,
      columnDef.list,
      undefined,
      lookupColumnKey
    )
  }
}

export const TableRemoveRowFromData = (
  defId: SYSTableDefIds,
  key: string,
  multi?: { isFirst: boolean; isLast: boolean }
) => {
  let data: SYSTableData = SYSConfig.app.tableDefIds[defId].getData(
    ovl.getState()
  )

  let def = data.tableDef[defId]
  ovl.getActions().ovl.internal._TableDeleteRowFromData({
    data: data,
    def,
    key,
  })

  // if (!multi || multi.isLast) {
  //   if (
  //     def.uiState.needsRefresh &&
  //     (def.features.autoRebuild === "AfterModifyOrDelete" ||
  //       def.features.autoRebuild === "AfterModifyOrDeleteOrAdd")
  //   ) {
  //     TableRebuild(def.id)
  //   }
  // }
  // def.uiState.rebuild
}

export type _CancelTableRowOptions = {
  formState?: SYSFormState
  promptIfDirty?: boolean
}

export const _TableFormCloseEditRow = async (
  defId: SYSTableDefIds,
  key: string,
  options?: _CancelTableRowOptions
) => {
  let data: SYSTableData = SYSConfig.app.tableDefIds[defId].getData(
    ovl.getState()
  )

  if (options?.formState) {
    let fn =
      ovl.getActions().app.features[options.formState.feature]?.hooks?.[
        HookFormBeforeCancel
      ]
    if (fn) {
      let hookRes: HookFormBeforeCancel_ReturnType = await fn(<
        HookFormBeforeCancel_Type
      >{
        formState: options.formState,
      })
      if (hookRes.resolve === "Cancel") {
        return
      }
    }
    if (options?.promptIfDirty) {
      if (FormIsDirty(options.formState)) {
        if ((await DialogOkCancel(T("SYSCancelForm"))) === 2) {
          return
        }
      }
    }
    fn =
      ovl.getActions().app.features[options.formState.feature]?.hooks?.[
        HookFormAfterCancel
      ]
    if (fn) {
      await fn(<HookFormAfterCancel_Type>{
        formState: options.formState,
      })
    }

    DialogClose("SYSTableRowEdit", { instance: `${defId}_${key}` })

    if (!options?.formState.forceKeepFormStateOnCancel) {
      setTimeout(() => {
        ovl.getActions().ovl.form._RemoveForm(options.formState)
      }, 200)
    }
  }
  let ovlSysState = data.data[key].OvlSysState as OvlSysColumn
  let rowState = ovlSysState.rowState
  if (rowState) {
    let isAdd = rowState.state === "Add"
    if (isAdd) {
      ovl.getActions().ovl.internal._TableDeleteRowFromData({
        key,
        def: data.tableDef[defId],
        data,
      })
      // // remove any save error feedback
      // delete ovlSysState.fnFeedback["OvlSaveRow"]
    }
  }

  // this simply manages the edit sysOvlState
  ovl.getActions().ovl.table._TableEditClose({
    key,
    tableDef: data.tableDef[defId],
    data,
  })
}

export type _SortTableByOptions = {
  order?: SortDirection
  isCustom?: boolean
  showProcess?: boolean
  useValue?: boolean
}

export const TableGetTotalResult = (
  aggregateCode: string = undefined,
  tableDefId: SYSTableDefIds = undefined
): _TableGroupAggregateResultType => {
  if (tableDefId === undefined) {
    tableDefId = _HookTableGroupDefId
  }
  if (aggregateCode === undefined) {
    aggregateCode = "@@SYSTotal"
  }
  return TableGetGroupValueTotalResult(
    aggregateCode,
    "@@SYSTotal",
    undefined,
    tableDefId
  )
}

export const TableGetGroupTotalResult = (
  aggregateCode: string | undefined = undefined,
  groupCode: string | undefined = undefined,
  tableDefId: SYSTableDefIds = undefined
): _TableGroupAggregateResultType => {
  if (tableDefId === undefined) {
    tableDefId = _HookTableGroupDefId
  }
  if (aggregateCode === undefined) {
    aggregateCode = "@@SYSTotal"
  }
  if (groupCode === undefined) {
    groupCode = "@@SYSTotal"
  }
  return TableGetGroupValueTotalResult(
    aggregateCode,
    "@@SYSTotal",
    undefined,
    tableDefId
  )
}

export const TableGetGroupValueTotalResult = (
  aggregateCode: string | undefined,
  groupCode: string,
  groupValue: string,
  tableDefId: SYSTableDefIds = undefined
): _TableGroupAggregateResultType => {
  if (tableDefId === undefined) {
    tableDefId = _HookTableGroupDefId
  }

  let tableDef: SYSTableDef = undefined
  tableDef = TableGet(tableDefId).dataDef.tableDef[tableDefId]
  let initresult = _TableGetGroupInitAggregateResult()

  if (tableDef.uiState.rebuild.needsRefresh) {
    return initresult
  }

  let tableGroupResults = tableDef.uiState.tableGroupResults

  if (aggregateCode === undefined) {
    aggregateCode = "@@SYSTotal"
  }

  if (!tableGroupResults[aggregateCode]) {
    // if the aggregate is not ready just return initresult
    return initresult
  }

  if (!tableGroupResults[aggregateCode][groupCode]) {
    // if the aggregate is not ready just return initresult
    return initresult
  }

  if (groupValue === undefined) {
    let results: _TableGroupAggregateResultType[] = []
    Object.keys(tableGroupResults[aggregateCode][groupCode]).forEach(
      (groupValue) => {
        results.push(tableGroupResults[aggregateCode][groupCode][groupValue])
      }
    )
    return results.reduce((val, result) => {
      let isString = typeof val.Sum === "string"
      if (val.Sum === undefined) {
        if (isString) {
          val.Sum = ""
        } else {
          val.Sum = 0
        }
      }
      val.Sum += result.Sum
      val.Count += result.Count
      if (result.Min < val.Min || val.Min === undefined) {
        val.Min = result.Min
      }
      if (result.Max > val.Max || val.Max === undefined) {
        val.Max = result.Max
      }
      if (!isString) {
        val.Avg = (val.Avg + result.Avg) / 2
      }
      return val
    }, initresult)
  } else {
    if (!tableGroupResults[aggregateCode][groupCode][groupValue]) {
      return initresult
    }
    return tableGroupResults[aggregateCode][groupCode][groupValue]
  }
}
export const TableGetGroupResult = (
  aggregateCode: string | undefined,
  groupValue: string | string[],
  tableDefId: SYSTableDefIds = undefined
): _TableGroupAggregateResultType => {
  if (tableDefId === undefined) {
    tableDefId = _HookTableGroupDefId
  }
  let tableDef: SYSTableDef = undefined
  tableDef = TableGet(tableDefId).dataDef.tableDef[tableDefId]
  let initresult = _TableGetGroupInitAggregateResult()
  if (tableDef.uiState.rebuild.needsRefresh) {
    return initresult
  }
  let tableGroupResults = tableDef.uiState.tableGroupResults
  if (aggregateCode === undefined) {
    aggregateCode = "@@SYSTotal"
  }
  if (!tableGroupResults[aggregateCode]) {
    // if the aggregate is not ready just return initresult
    return initresult
  }

  //now we have table defined

  //@@todo if a string of the group array in between is like "@@SYSAll" (or event the group as submitted is "@@SYSAll" then return array of results (user needs then to reduce (eg. sum) em all up)

  let groupLength = 2
  let groupValueKey
  let aggregateGroupKeys = tableDef.options.tableGroupAggregates.groups
  if (typeof groupValue === "string") {
    groupValueKey = `1${groupValue}`
  } else {
    groupValue = <string[]>JSON.parse(JSON.stringify(groupValue))
    groupValue.unshift("1")
    groupLength = groupValue.length
    groupValueKey = "".concat(...groupValue)
  }
  let groupKey = ""

  for (let z = 0; z < groupLength; z++) {
    let groupCode = aggregateGroupKeys[z].groupCode
    groupKey = `${groupKey}${groupCode}`
  }

  // it can be that the aggregate is initalised but no rows and therefore no groups are calculated
  if (!tableGroupResults[aggregateCode][groupKey]) {
    return initresult
  }
  if (!tableGroupResults[aggregateCode][groupKey][groupValueKey]) {
    return initresult
  }
  return tableGroupResults[aggregateCode][groupKey][groupValueKey]
}

export type { SYSColumnDef, SYSTableDef } from "../../library/table/table"
export const TableStaticFilterGet = <T = any>(
  tableDefId: SYSTableDefIds
): { [key in keyof T]: any } => {
  return <{ [key in keyof T]: any }>(
    TableGet(tableDefId).def.uiState?.filter?.filter?.static
  )
}

export const TableStaticFilterSetEntry = <T = any>(
  tableDefId: SYSTableDefIds,
  filterKey: string,
  filterValue: any
): { [key in keyof T]: any } => {
  let tb = TableGet(tableDefId)
  let staticFilter = tb.def.uiState?.filter?.filter?.static
  if (staticFilter === undefined) {
    // not initilised yet so just set it on options
    if (tb.def.options === undefined) {
      tb.def.options = {
        initial_filter: {
          static: {},
        },
      }
    } else if (tb.def.options.initial_filter === undefined) {
      tb.def.options.initial_filter = {
        static: {},
      }
    } else if (tb.def.options.initial_filter.static === undefined) {
      {
        tb.def.options.initial_filter.static = {}
      }
    }
    staticFilter = tb.def.options.initial_filter.static
  }
  staticFilter[filterKey] = filterValue
  return <{ [key in keyof T]: any }>staticFilter
}

export const TableStaticFilterRemoveEntry = <T = any>(
  tableDefId: SYSTableDefIds,
  filterKey: string
): { [key in keyof T]: any } => {
  let staticFilter = TableGet(tableDefId).def.uiState?.filter?.filter?.static
  if (staticFilter === undefined) {
    // not initilised yet so just set it on options
    staticFilter = TableGet(tableDefId).def.options.initial_filter.static
  }
  delete staticFilter[filterKey]
  return <{ [key in keyof T]: any }>staticFilter
}

export const TableStaticFilterReset = <T = any>(
  tableDefId: SYSTableDefIds
): { [key in keyof T]: any } => {
  let table = TableGet(tableDefId)
  let staticFilter = table.def.uiState.filter.filter.static
  staticFilter = JSON.parse(
    JSON.stringify(table.def.options.initial_filter.static)
  )
  return <{ [key in keyof T]: any }>staticFilter
}

export const TableSchemaGet = async <RowType = any>(
  defId: SYSTableDefIds
): Promise<{ [key in keyof RowType]: Schema }> => {
  let table = TableGet(defId)
  let def = table.def
  if (
    (def.database?.useSchema === undefined ||
      def.database?.useSchema === true) &&
    !def.uiState?._schemaRetrieved &&
    (!table.dataDef.schema || Object.keys(table.dataDef.schema).length === 0)
  ) {
    try {
      // do it in a try catch if an endpoint is not prepared for that...
      table.dataDef.schema = (
        await Data(def.server.endpoint + "/get", {
          justSchema: true,
          getSchema: true,
          filter: {},
          data: {},
        })
      ).data.schema
      if (!table.dataDef.schema) {
        table.dataDef.schema = {}
      }
      if (def.uiState) {
        def.uiState._schemaRetrieved = true
      }
    } catch (e) {
      // this is currently just a temporary fix for when endpoints are not ready for the SchemaGet
      if (e.status === 400) {
        if (def.uiState) {
          def.uiState._schemaRetrieved = true
        }
      }
      table.dataDef.schema = {}
      console.error(e)
    }
    if (def.uiState) {
      def.uiState._schemaRetrieved = true
    }
    let fn =
      ovl.getActions().app.features?.[def.feature]?.hooks?.[
        HookTableSchemaAssign
      ]
    if (fn) {
      if (!table.dataDef.schema) {
        table.dataDef.schema = {}
      }

      await fn(<HookTableSchemaAssign_Type>{
        tableDefId: defId,
        schemaToSet: table.dataDef.schema,
      })
      if (table.dataDef.schema === null || table.dataDef.schema === undefined) {
        table.dataDef.schema = {}
      }
    }
  }
  return <{ [key in keyof RowType]: Schema }>table.dataDef.schema
}

export const TableOpenColumnFilter = async (
  defId: SYSTableDefIds,
  columnKey: string,
  options?: { allowExclusive?: boolean }
) => {
  let state = ovl.getState()
  state.ovl.libState.tableSettings.columnFilter = {
    columnKey,
    additionalColumnKey: "",
    defId,
    allowExclusive: options?.allowExclusive,
  }

  let table = TableGet(defId)
  // this needs to be set to undefined because its used as a cache and undefined resets it
  _BaseTableContextForColumnFilter.tableDef = undefined
  tblTableSettings_ColumnFilter.columns["Code"].type =
    table.def.columns[columnKey].type
  // tblTableSettings_ColumnFilter.columns["Code"].ui.labelTranslationKey =
  //   this.tableDef.def.columns[this.columnKey].ui.labelTranslationKey
  tblTableSettings_ColumnFilter.columns["Free1"].ui.visibility = "None"

  let filter = table.def.uiState.filter.columns[columnKey].columnFilter

  let additionalColumn = filter.additionalColumn
  if (additionalColumn) {
    state.ovl.libState.tableSettings.columnFilter.additionalColumnKey =
      additionalColumn
    tblTableSettings_ColumnFilter.columns["Free1"].type =
      table.def.columns[additionalColumn].type
    tblTableSettings_ColumnFilter.columns["Free1"].ui.visibility = "Table"
    // tblTableSettings_ColumnFilter.columns["Free1"].ui.labelTranslationKey =
    //   this.tableDef.def.columns[additionalColumn].ui.labelTranslationKey
  }

  //create filter source data
  let rows = table.rows
  let sort = 0
  let nullHandling = table.def.columns[columnKey].nullHandling.enabled

  let keys = _TableFilterFn({ def: table.def, data: table.dataDef }, columnKey)

  //let isCaluclated = table.def.columns[columnKey].schema?.isCalculatedColumn

  // add selected to keys
  let localData0 = keys.reduce(
    (val: { [key: string]: SYSTableSettings_ColumnFilter }, k) => {
      let code
      //if (isCaluclated) {
      code = GetRowRawValue(table.def, columnKey, rows[k], k, "ColumnFilter")
      // } else {
      //   code = rows[k][columnKey]
      // }
      if (!nullHandling && code === null) {
        code = ""
      }
      if (val[code]) {
        val[code].Count++
      } else {
        sort++
        val[code] = {
          TableDefId: defId,
          Key: k,
          Code: code,
          Free1: additionalColumn ? rows[k][additionalColumn] : null,
          /*Selected: false,*/
          Count: 1,
          OriginalSort: sort,
        }
      }
      return val
    },
    {}
  )
  // make sure selected are always in list
  if (filter.list) {
    filter.list.forEach((k) => {
      if (rows[k] === undefined || localData0[k]) {
        return
      }
      let foundKey
      Object.keys(rows).some((f) => {
        let val = rows[f][columnKey]
        if (val === k || (!nullHandling && k === "" && val === null)) {
          foundKey = f
        }
      })

      localData0[k] = {
        TableDefId: defId,
        Key: foundKey,
        Code: k,
        Free1: additionalColumn ? rows[k][additionalColumn] : null,
        /*Selected: false,*/
        Count: 0,
        OriginalSort: 0,
      }
    })
  }

  let localData = Object.keys(localData0).map((k) => localData0[k])
  await TableRefresh("SYSTableSettings_ColumnFilter", {
    localData,
    staticFilter: { TableDefId: defId },
    resetStrategy: { Selection: true },
    showSnack: false,
  })

  let td = TableGet<SYSTableSettings_ColumnFilter>(
    "SYSTableSettings_ColumnFilter"
  )
  // now set all the selected to true
  if (filter.list) {
    filter.list.forEach((k) => {
      /*localData0[k].Selected = true*/
      let localKey = td.getKeys({ byIdValue: k })[0]
      if (localKey !== undefined) {
        TableSelectRow("SYSTableSettings_ColumnFilter", localKey, {
          mode: "Select",
        })
      }
    })
  }

  DialogOpen("SYSTableSettings_ColumnFilter")
}
